import { Plus } from "lucide-react";
import { type ChangeEvent, useContext, useRef } from "react";
import type { NavigateFunction } from "react-router-dom";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/app/components/ui/tooltip";
import ResumeContext from "@/app/context/ResumeContext";
import { cn } from "@/app/lib/utils";
import type { ResumeContextValue, SelectedExtras } from "@/app/types/resume";

export interface ExtrasSection {
	id: string;
	label: string;
	name: string;
	link: string;
}

interface ExtrasCheckboxProps {
	section: ExtrasSection;
	extrasChoicesHandler: (event: ChangeEvent<HTMLInputElement>) => void;
	navigate: NavigateFunction;
}

function ExtrasCheckbox({ section, extrasChoicesHandler, navigate }: ExtrasCheckboxProps) {
	const checkBoxRef = useRef<HTMLInputElement>(null);
	const { resumeData } = useContext(ResumeContext) as ResumeContextValue;
	const selectedExtras = resumeData.extras.selectedExtras;

	const isChecked = Boolean(selectedExtras[section.id as keyof SelectedExtras]);

	return (
		<div
			className={cn(
				"mb-3 flex cursor-pointer items-center justify-between rounded-md border border-gray-300 border-dashed px-3 py-2 transition-colors hover:border-primary-500",
				isChecked && "bg-primary-50",
			)}
			onClick={() => {
				checkBoxRef.current?.click();
			}}
		>
			<p className="m-0 font-semibold text-gray-900">{section.label}</p>
			<input
				type="checkbox"
				className="hidden"
				ref={checkBoxRef}
				id={section.name}
				name={section.name}
				checked={isChecked}
				onChange={extrasChoicesHandler}
			/>
			{isChecked ? (
				<Tooltip>
					<TooltipTrigger asChild>
						<span
							onClick={(event) => {
								// Don't bubble to the wrapper, which would toggle the
								// section selection when the user only meant to edit it.
								event.stopPropagation();
								navigate(section.link);
							}}
							className="cursor-pointer font-semibold text-primary-700"
							role="button"
							tabIndex={0}
							onKeyDown={(event) => {
								if (event.key === "Enter" || event.key === " ") {
									event.stopPropagation();
									navigate(section.link);
								}
							}}
						>
							Update
						</span>
					</TooltipTrigger>
					<TooltipContent side="right" variant="plain">
						Click to Add/Edit
					</TooltipContent>
				</Tooltip>
			) : (
				<span>
					<Plus className="size-4 text-gray-600" />
				</span>
			)}
		</div>
	);
}

export default ExtrasCheckbox;
